import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Custom SVG Icons
const Icons = {
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  userPlus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  crown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 17l5-5 4 4 5-5 5 5" />
      <path d="M2 17v4h20v-4" />
      <path d="M12 7a2 2 0 100-4 2 2 0 000 4z" />
    </svg>
  ),
  checkCircle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  trendDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  messageSquare: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  fileText: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
};

// Donut Chart Component
const DonutChart = ({ data, size = 160, strokeWidth = 24, centerLabel, centerValue }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercent = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((segment, i) => {
          const percent = segment.value / total;
          const strokeDasharray = `${percent * circumference} ${circumference}`;
          const rotation = cumulativePercent * 360 - 90;
          cumulativePercent += percent;

          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          );
        })}
      </svg>
      {centerLabel && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {centerValue}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
            {centerLabel}
          </div>
        </div>
      )}
    </div>
  );
};

// Funnel Chart Component
const FunnelChart = ({ stages, maxWidth = 400 }) => {
  const maxValue = Math.max(...stages.map(s => s.reached_count || s.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      {stages.map((stage, i) => {
        const value = stage.reached_count || stage.count;
        const widthPercent = (value / maxValue) * 100;

        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <span style={{
              width: '100px',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              textAlign: 'right',
              flexShrink: 0,
            }}>
              {stage.stage || stage.name}
            </span>
            <div style={{
              flex: 1,
              background: 'var(--bg-deep)',
              borderRadius: 'var(--radius-md)',
              height: '28px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.max(widthPercent, 3)}%`,
                height: '100%',
                background: `linear-gradient(90deg, var(--indigo-500), var(--cyan-500))`,
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 'var(--space-sm)',
                transition: 'width 0.5s ease',
              }}>
                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>
                  {value}
                </span>
              </div>
            </div>
            <span style={{
              width: '50px',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              flexShrink: 0,
            }}>
              {stage.percentage}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Bar Chart Component
const BarChart = ({ data, labelKey = 'label', valueKey = 'value' }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey]), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {data.map((item, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item[labelKey]}</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{item[valueKey]}</span>
          </div>
          <div style={{
            background: 'var(--bg-deep)',
            borderRadius: 'var(--radius-sm)',
            height: '8px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${(item[valueKey] / maxValue) * 100}%`,
              height: '100%',
              background: item.color || 'var(--gradient-primary)',
              borderRadius: 'var(--radius-sm)',
              transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const { user, session, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Admin check state
  const [isAdmin, setIsAdmin] = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Data states
  const [overview, setOverview] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('30d');

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const token = getToken();
      if (!token) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/check`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };

    if (!authLoading && session && user) {
      checkAdminStatus();
    }
  }, [authLoading, session, user, getToken]);

  // Load data when admin is confirmed
  useEffect(() => {
    if (isAdmin) {
      loadAllData();
    }
  }, [isAdmin, period]);

  const loadAllData = async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [overviewRes, onboardingRes, engagementRes, pipelineRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/overview?period=${period}`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/onboarding`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/engagement?period=${period}`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/pipeline`, { headers }),
      ]);

      if (!overviewRes.ok || !onboardingRes.ok || !engagementRes.ok || !pipelineRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [overviewData, onboardingData, engagementData, pipelineData] = await Promise.all([
        overviewRes.json(),
        onboardingRes.json(),
        engagementRes.json(),
        pipelineRes.json(),
      ]);

      setOverview(overviewData);
      setOnboarding(onboardingData);
      setEngagement(engagementData);
      setPipeline(pipelineData);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Loading states
  if (authLoading || checkingAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  // Not logged in
  if (!user || !session) {
    navigate('/login');
    return null;
  }

  // Not admin
  if (!isAdmin) {
    return (
      <DashboardLayout title="Access Denied">
        <div style={{
          padding: 'var(--space-4xl)',
          textAlign: 'center',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-md)'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--red-500)" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)' }}>
            Admin Access Required
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-tertiary)' }}>
            You don't have permission to access analytics.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const styles = {
    periodSelector: {
      display: 'flex',
      gap: 'var(--space-xs)',
      marginBottom: 'var(--space-xl)',
    },
    periodButton: (isActive) => ({
      padding: 'var(--space-sm) var(--space-md)',
      background: isActive ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-surface)',
      border: `1px solid ${isActive ? 'rgba(99, 102, 241, 0.3)' : 'var(--border-subtle)'}`,
      borderRadius: 'var(--radius-md)',
      color: isActive ? 'var(--indigo-400)' : 'var(--text-secondary)',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      fontFamily: 'var(--font-body)',
      transition: 'all var(--duration-fast)',
    }),
    kpiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: 'var(--space-lg)',
      marginBottom: 'var(--space-2xl)',
    },
    kpiCard: {
      background: 'var(--bg-card)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--space-xl)',
    },
    kpiIcon: (color) => ({
      width: '40px',
      height: '40px',
      background: `${color}20`,
      borderRadius: 'var(--radius-md)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 'var(--space-md)',
      color: color,
    }),
    kpiValue: {
      fontFamily: 'var(--font-display)',
      fontSize: '2rem',
      fontWeight: 700,
      color: 'var(--text-primary)',
      marginBottom: 'var(--space-xs)',
    },
    kpiLabel: {
      fontSize: '0.875rem',
      color: 'var(--text-tertiary)',
    },
    kpiTrend: (positive) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--space-xs)',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: positive ? 'var(--green-500)' : 'var(--red-500)',
      marginTop: 'var(--space-sm)',
    }),
    tabBar: {
      display: 'flex',
      gap: 'var(--space-sm)',
      marginBottom: 'var(--space-xl)',
      borderBottom: '1px solid var(--border-subtle)',
      paddingBottom: 'var(--space-md)',
      overflowX: 'auto',
    },
    tab: (isActive) => ({
      padding: 'var(--space-sm) var(--space-lg)',
      background: isActive ? 'var(--gradient-primary)' : 'transparent',
      color: isActive ? 'white' : 'var(--text-secondary)',
      border: 'none',
      borderRadius: 'var(--radius-md)',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: 'var(--font-body)',
      whiteSpace: 'nowrap',
      transition: 'all var(--duration-fast)',
    }),
    section: {
      background: 'var(--bg-card)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--space-xl)',
      marginBottom: 'var(--space-xl)',
    },
    sectionTitle: {
      fontFamily: 'var(--font-display)',
      fontSize: '1.125rem',
      fontWeight: 600,
      color: 'var(--text-primary)',
      marginBottom: 'var(--space-lg)',
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: 'var(--space-xl)',
    },
    chartContainer: {
      minHeight: '200px',
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'var(--space-md)',
      marginTop: 'var(--space-lg)',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-xs)',
      fontSize: '0.8rem',
      color: 'var(--text-secondary)',
    },
    legendDot: (color) => ({
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: color,
    }),
    statRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: 'var(--space-md) 0',
      borderBottom: '1px solid var(--border-subtle)',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: 'var(--text-secondary)',
    },
    statValue: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--text-primary)',
    },
  };

  const tierColors = {
    trial: 'var(--amber-400)',
    free: 'var(--text-tertiary)',
    premium: 'var(--indigo-400)',
  };

  const careerColors = {
    'Student': 'var(--cyan-400)',
    'Recent Graduate': 'var(--indigo-400)',
    'Currently Employed': 'var(--green-500)',
    'Between Roles': 'var(--amber-400)',
    'Unknown': 'var(--text-muted)',
  };

  return (
    <DashboardLayout
      title="Analytics Dashboard"
      subtitle="Product metrics and user insights"
    >
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--red-500)',
          marginBottom: 'var(--space-lg)'
        }}>
          {error}
        </div>
      )}

      {/* Period Selector */}
      <div style={styles.periodSelector}>
        {['7d', '30d', '90d', 'all'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={styles.periodButton(period === p)}
          >
            {p === 'all' ? 'All Time' : p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-4xl)', color: 'var(--text-tertiary)' }}>
          Loading analytics...
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={styles.kpiGrid}>
            <div style={styles.kpiCard}>
              <div style={styles.kpiIcon('var(--indigo-400)')}>
                <div style={{ width: '20px', height: '20px' }}>{Icons.users}</div>
              </div>
              <div style={styles.kpiValue}>{overview?.total_users || 0}</div>
              <div style={styles.kpiLabel}>Total Users</div>
            </div>

            <div style={styles.kpiCard}>
              <div style={styles.kpiIcon('var(--cyan-400)')}>
                <div style={{ width: '20px', height: '20px' }}>{Icons.userPlus}</div>
              </div>
              <div style={styles.kpiValue}>{overview?.new_users || 0}</div>
              <div style={styles.kpiLabel}>New Users ({period})</div>
              {overview?.new_users_trend !== undefined && (
                <div style={styles.kpiTrend(overview.new_users_trend >= 0)}>
                  <div style={{ width: '14px', height: '14px' }}>
                    {overview.new_users_trend >= 0 ? Icons.trendUp : Icons.trendDown}
                  </div>
                  {Math.abs(overview.new_users_trend)}% vs prev
                </div>
              )}
            </div>

            <div style={styles.kpiCard}>
              <div style={styles.kpiIcon('var(--green-500)')}>
                <div style={{ width: '20px', height: '20px' }}>{Icons.activity}</div>
              </div>
              <div style={styles.kpiValue}>{overview?.active_users_7d || 0}</div>
              <div style={styles.kpiLabel}>Active Users (7d)</div>
            </div>

            <div style={styles.kpiCard}>
              <div style={styles.kpiIcon('var(--amber-400)')}>
                <div style={{ width: '20px', height: '20px' }}>{Icons.crown}</div>
              </div>
              <div style={styles.kpiValue}>{overview?.tier_distribution?.premium || 0}</div>
              <div style={styles.kpiLabel}>Premium Users</div>
            </div>

            <div style={styles.kpiCard}>
              <div style={styles.kpiIcon('var(--cyan-400)')}>
                <div style={{ width: '20px', height: '20px' }}>{Icons.checkCircle}</div>
              </div>
              <div style={styles.kpiValue}>{overview?.onboarding_rate || 0}%</div>
              <div style={styles.kpiLabel}>Onboarding Rate</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={styles.tabBar}>
            {['overview', 'users', 'engagement', 'pipeline'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={styles.tab(activeTab === tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={styles.grid2}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Tier Distribution</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)' }}>
                  <DonutChart
                    data={[
                      { value: overview?.tier_distribution?.trial || 0, color: tierColors.trial },
                      { value: overview?.tier_distribution?.free || 0, color: tierColors.free },
                      { value: overview?.tier_distribution?.premium || 0, color: tierColors.premium },
                    ]}
                    centerValue={overview?.total_users || 0}
                    centerLabel="Total"
                  />
                  <div style={styles.legend}>
                    {Object.entries(overview?.tier_distribution || {}).map(([tier, count]) => (
                      <div key={tier} style={styles.legendItem}>
                        <div style={styles.legendDot(tierColors[tier])} />
                        <span>{tier}: {count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Feature Usage ({period})</h3>
                <BarChart
                  data={[
                    { label: 'Mock Interviews', value: engagement?.mock_interviews?.total_sessions || 0, color: 'linear-gradient(90deg, var(--indigo-500), var(--indigo-400))' },
                    { label: 'Quiz Sessions', value: engagement?.quiz_sessions?.total_sessions || 0, color: 'linear-gradient(90deg, var(--cyan-500), var(--cyan-400))' },
                    { label: 'Resume Practice', value: engagement?.resume_practice?.total_sessions || 0, color: 'linear-gradient(90deg, var(--amber-500), var(--amber-400))' },
                    { label: 'AI Resumes', value: engagement?.ai_resumes_generated || 0, color: 'linear-gradient(90deg, var(--green-500), var(--green-400))' },
                    { label: 'STAR Stories', value: engagement?.star_stories_created || 0, color: 'linear-gradient(90deg, var(--red-500), var(--red-400))' },
                  ]}
                />
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Onboarding Funnel</h3>
                {onboarding?.step_funnel && <FunnelChart stages={onboarding.step_funnel} />}
              </div>

              <div style={styles.grid2}>
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Career Status</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)' }}>
                    <DonutChart
                      data={Object.entries(onboarding?.career_status || {}).map(([status, count]) => ({
                        value: count,
                        color: careerColors[status] || 'var(--text-muted)',
                      }))}
                      centerValue={onboarding?.total_users || 0}
                      centerLabel="Users"
                    />
                    <div style={{ flex: 1 }}>
                      {Object.entries(onboarding?.career_status || {}).map(([status, count]) => (
                        <div key={status} style={styles.statRow}>
                          <span style={styles.statLabel}>{status}</span>
                          <span style={styles.statValue}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Primary Goal</h3>
                  <div style={{ flex: 1 }}>
                    {Object.entries(onboarding?.primary_goal || {}).map(([goal, count]) => (
                      <div key={goal} style={styles.statRow}>
                        <span style={styles.statLabel}>{goal}</span>
                        <span style={styles.statValue}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Years of Experience</h3>
                  <BarChart
                    data={Object.entries(onboarding?.years_experience || {})
                      .filter(([k]) => k !== 'Unknown')
                      .map(([bucket, count]) => ({ label: bucket + ' yrs', value: count }))}
                  />
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Remote Preference</h3>
                  <div style={{ flex: 1 }}>
                    {Object.entries(onboarding?.remote_preference || {}).map(([pref, count]) => (
                      <div key={pref} style={styles.statRow}>
                        <span style={styles.statLabel}>{pref}</span>
                        <span style={styles.statValue}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Job Search Challenges - User Feedback */}
              {onboarding?.job_search_challenges?.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      <div style={{ width: '20px', height: '20px', color: 'var(--amber-400)' }}>
                        {Icons.messageSquare}
                      </div>
                      Job Search Challenges ({onboarding.job_search_challenges.length})
                    </div>
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-lg)' }}>
                    User feedback on their biggest job search challenges (from onboarding)
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', maxHeight: '500px', overflowY: 'auto' }}>
                    {onboarding.job_search_challenges.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          background: 'var(--bg-deep)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-lg)',
                          padding: 'var(--space-lg)',
                        }}
                      >
                        <p style={{
                          fontSize: '0.95rem',
                          color: 'var(--text-primary)',
                          lineHeight: 1.6,
                          marginBottom: 'var(--space-md)',
                        }}>
                          "{item.feedback}"
                        </p>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'var(--space-sm)',
                          fontSize: '0.75rem',
                        }}>
                          {item.career_status && (
                            <span style={{
                              background: 'rgba(99, 102, 241, 0.15)',
                              color: 'var(--indigo-400)',
                              padding: 'var(--space-xs) var(--space-sm)',
                              borderRadius: 'var(--radius-sm)',
                            }}>
                              {item.career_status}
                            </span>
                          )}
                          {item.primary_goal && (
                            <span style={{
                              background: 'rgba(34, 211, 238, 0.15)',
                              color: 'var(--cyan-400)',
                              padding: 'var(--space-xs) var(--space-sm)',
                              borderRadius: 'var(--radius-sm)',
                            }}>
                              {item.primary_goal}
                            </span>
                          )}
                          {item.created_at && (
                            <span style={{
                              color: 'var(--text-muted)',
                              padding: 'var(--space-xs) var(--space-sm)',
                            }}>
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Engagement Tab */}
          {activeTab === 'engagement' && (
            <div style={styles.grid2}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Mock Interviews</h3>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Total Sessions</span>
                  <span style={styles.statValue}>{engagement?.mock_interviews?.total_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Completed</span>
                  <span style={styles.statValue}>{engagement?.mock_interviews?.completed_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Average Score</span>
                  <span style={styles.statValue}>{engagement?.mock_interviews?.average_score || 0}</span>
                </div>
                <h4 style={{ ...styles.sectionTitle, fontSize: '0.9rem', marginTop: 'var(--space-lg)' }}>Hiring Decisions</h4>
                <BarChart
                  data={[
                    { label: 'Hire', value: engagement?.mock_interviews?.hiring_decisions?.hire || 0, color: 'var(--green-500)' },
                    { label: 'Maybe', value: engagement?.mock_interviews?.hiring_decisions?.maybe || 0, color: 'var(--amber-400)' },
                    { label: 'No Hire', value: engagement?.mock_interviews?.hiring_decisions?.no_hire || 0, color: 'var(--red-500)' },
                  ]}
                />
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Quiz Sessions</h3>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Total Sessions</span>
                  <span style={styles.statValue}>{engagement?.quiz_sessions?.total_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Completed</span>
                  <span style={styles.statValue}>{engagement?.quiz_sessions?.completed_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Average Score</span>
                  <span style={styles.statValue}>{engagement?.quiz_sessions?.average_score || 0}%</span>
                </div>
                <h4 style={{ ...styles.sectionTitle, fontSize: '0.9rem', marginTop: 'var(--space-lg)' }}>By Type</h4>
                <BarChart
                  data={[
                    { label: 'Study', value: engagement?.quiz_sessions?.by_type?.study || 0 },
                    { label: 'Quiz', value: engagement?.quiz_sessions?.by_type?.quiz || 0 },
                    { label: 'Daily Challenge', value: engagement?.quiz_sessions?.by_type?.daily_challenge || 0 },
                  ]}
                />
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Resume Practice</h3>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Total Sessions</span>
                  <span style={styles.statValue}>{engagement?.resume_practice?.total_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Completed</span>
                  <span style={styles.statValue}>{engagement?.resume_practice?.completed_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Average Score</span>
                  <span style={styles.statValue}>{engagement?.resume_practice?.average_score || 0}</span>
                </div>
                <h4 style={{ ...styles.sectionTitle, fontSize: '0.9rem', marginTop: 'var(--space-lg)' }}>By Mode</h4>
                <BarChart
                  data={[
                    { label: 'Coach', value: engagement?.resume_practice?.by_mode?.coach || 0 },
                    { label: 'Interview', value: engagement?.resume_practice?.by_mode?.interview || 0 },
                  ]}
                />
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Other Features</h3>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>AI Resumes Generated</span>
                  <span style={styles.statValue}>{engagement?.ai_resumes_generated || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>STAR Stories Created</span>
                  <span style={styles.statValue}>{engagement?.star_stories_created || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Company Research</span>
                  <span style={styles.statValue}>{engagement?.company_research_sessions || 0}</span>
                </div>
                <div style={styles.statRow}>
                  <span style={styles.statLabel}>Total Applications</span>
                  <span style={styles.statValue}>{engagement?.total_applications || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Pipeline Tab */}
          {activeTab === 'pipeline' && (
            <>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Application Pipeline (All Users)</h3>
                {pipeline?.funnel && <FunnelChart stages={pipeline.funnel} />}
              </div>

              <div style={styles.grid2}>
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Status Distribution</h3>
                  {Object.entries(pipeline?.status_counts || {}).map(([status, count]) => (
                    <div key={status} style={styles.statRow}>
                      <span style={styles.statLabel}>{status}</span>
                      <span style={styles.statValue}>{count}</span>
                    </div>
                  ))}
                </div>

                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Conversion Rates</h3>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>Wishlist to Applied</span>
                    <span style={styles.statValue}>{pipeline?.conversion_rates?.wishlist_to_applied || 0}%</span>
                  </div>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>Applied to Interview</span>
                    <span style={styles.statValue}>{pipeline?.conversion_rates?.applied_to_interview || 0}%</span>
                  </div>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>Interview to Offer</span>
                    <span style={styles.statValue}>{pipeline?.conversion_rates?.interview_to_offer || 0}%</span>
                  </div>
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>Offer to Accepted</span>
                    <span style={styles.statValue}>{pipeline?.conversion_rates?.offer_to_accepted || 0}%</span>
                  </div>
                  <div style={{ ...styles.statRow, borderBottom: 'none', marginTop: 'var(--space-md)' }}>
                    <span style={{ ...styles.statLabel, color: 'var(--red-500)' }}>Rejected</span>
                    <span style={{ ...styles.statValue, color: 'var(--red-500)' }}>{pipeline?.rejected_count || 0}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default AdminAnalytics;
